/**
 * Shared UI atoms for the Material Browser dialogs.
 */

import { ReactNode } from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';
import {
    AppearanceAsset,
    FillPattern,
    getFillPattern,
} from '../../../core/MaterialTypes';

export const FIELD_LABEL_WIDTH = '150px';

/** Label + control row with BIM-editor spacing. */
export function EditorField({
    label,
    children,
    hint,
}: {
    label: string;
    children: ReactNode;
    hint?: string;
}) {
    return (
        <HStack spacing={3} align="start" py={1}>
            <Text
                fontSize="xs"
                color="var(--color-text-secondary)"
                width={FIELD_LABEL_WIDTH}
                flexShrink={0}
                pt={1}
                textAlign="end"
            >
                {label}
            </Text>
            <Box flex="1" minW={0}>
                {children}
                {hint ? (
                    <Text fontSize="2xs" color="var(--color-text-muted)" mt={0.5}>
                        {hint}
                    </Text>
                ) : null}
            </Box>
        </HStack>
    );
}

/** Small square color chip with a native color input overlaid. */
export function ColorSwatch({
    value,
    onChange,
    disabled,
    title,
}: {
    value: string;
    onChange?: (hex: string) => void;
    disabled?: boolean;
    title?: string;
}) {
    return (
        <Box position="relative" width="28px" height="20px" flexShrink={0} title={title}>
            <Box
                position="absolute"
                inset={0}
                bg={value}
                borderWidth="1px"
                borderColor="var(--color-border)"
                borderRadius="var(--cad-radius-sm)"
            />
            {onChange && !disabled ? (
                <Box
                    as="input"
                    type="color"
                    value={value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                    position="absolute"
                    inset={0}
                    opacity={0}
                    cursor="pointer"
                    aria-label={title ?? 'Pick color'}
                />
            ) : null}
        </Box>
    );
}

/**
 * SVG preview of a fill pattern (cut/surface hatch) — used in dropdowns,
 * thumbnails and editor panels. Mirrors the hatch shader styles 1:1.
 */
export function FillPatternPreview({
    pattern,
    foreground = '#303030',
    background = '#F0F0F0',
    size = 36,
}: {
    pattern: FillPattern | string | undefined;
    foreground?: string;
    background?: string;
    size?: number;
}) {
    const resolved = typeof pattern === 'string' ? getFillPattern(pattern) : pattern;
    const style = resolved?.style ?? 'None';
    const gap = 6; // px between hatch lines in preview space

    let content: ReactNode = null;
    if (style === 'Solid') {
        content = <rect x={0} y={0} width={size} height={size} fill={foreground} />;
    } else if (style === 'Parallel' || style === 'ANSI31' || style === 'Crosshatch') {
        const lines: ReactNode[] = [];
        const diagonal = size * 1.6;
        for (let offset = -diagonal; offset <= diagonal; offset += gap) {
            lines.push(
                <line
                    key={`l${offset}`}
                    x1={offset}
                    y1={0}
                    x2={offset}
                    y2={diagonal}
                    stroke={foreground}
                    strokeWidth={1}
                />,
            );
        }
        const baseAngle = style === 'ANSI31' ? 45 : (resolved?.angle ?? 45);
        content = (
            <g transform={`rotate(${-baseAngle} ${size / 2} ${size / 2})`}>
                {lines}
                {style === 'Crosshatch' ? (
                    <g transform={`rotate(90 ${size / 2} ${size / 2})`}>{lines}</g>
                ) : null}
            </g>
        );
    } else if (style === 'Brick') {
        const rows: ReactNode[] = [];
        const rowH = gap;
        for (let y = 0, row = 0; y <= size; y += rowH, row++) {
            rows.push(
                <line key={`h${y}`} x1={0} y1={y} x2={size} y2={y} stroke={foreground} strokeWidth={1} />,
            );
            const x0 = row % 2 === 0 ? 0 : gap * 1.5;
            for (let x = x0; x <= size; x += gap * 3) {
                rows.push(
                    <line
                        key={`t${y}-${x}`}
                        x1={x}
                        y1={y}
                        x2={x}
                        y2={Math.min(y + rowH, size)}
                        stroke={foreground}
                        strokeWidth={1}
                    />,
                );
            }
        }
        content = <g>{rows}</g>;
    } else if (style === 'Blocks') {
        const cells: ReactNode[] = [];
        const cell = gap * 2;
        for (let y = 0; y <= size; y += cell) {
            cells.push(<line key={`bh${y}`} x1={0} y1={y} x2={size} y2={y} stroke={foreground} strokeWidth={1} />);
        }
        for (let x = 0, row = 0; x <= size; x += cell, row++) {
            for (let y = (row % 2) * cell; y <= size; y += cell * 2) {
                cells.push(<line key={`bv${x}-${y}`} x1={x} y1={y} x2={x} y2={Math.min(y + cell * 2, size)} stroke={foreground} strokeWidth={1} />);
            }
        }
        content = <g>{cells}</g>;
    } else if (style === 'Dots') {
        const dots: ReactNode[] = [];
        for (let y = gap / 2; y < size; y += gap) {
            for (let x = gap / 2; x < size; x += gap) {
                dots.push(
                    <circle
                        key={`d${x}-${y}`}
                        cx={x + (((y / gap) | 0) % 2) * (gap / 2)}
                        cy={y}
                        r={1}
                        fill={foreground}
                    />,
                );
            }
        }
        content = <g>{dots}</g>;
    }
    return (
        <Box
            width={`${size}px`}
            height={`${size}px`}
            flexShrink={0}
            borderWidth="1px"
            borderColor="var(--color-border)"
            borderRadius="var(--cad-radius-sm)"
            overflow="hidden"
            bg={background}
        >
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="svg-block">
                <clipPath id={`clip-${style}-${size}`}>
                    <rect x={0} y={0} width={size} height={size} />
                </clipPath>
                <g clipPath={`url(#clip-${style}-${size})`}>{content}</g>
            </svg>
        </Box>
    );
}

/** Round appearance swatch: base color + roughness hint. */
export function AppearanceSwatch({ asset, size = 36 }: { asset: AppearanceAsset | undefined; size?: number }) {
    if (!asset) {
        return (
            <Box
                width={`${size}px`}
                height={`${size}px`}
                flexShrink={0}
                borderWidth="1px"
                borderColor="var(--color-border)"
                borderRadius="var(--cad-radius-sm)"
                bg="var(--color-bg-tertiary)"
            />
        );
    }
    const shine = Math.max(0, 1 - asset.roughness) * 0.5;
    return (
        <Box
            width={`${size}px`}
            height={`${size}px`}
            flexShrink={0}
            borderWidth="1px"
            borderColor="var(--color-border)"
            borderRadius="var(--cad-radius-sm)"
            opacity={1 - asset.transparency * 0.6}
            style={{
                background: `linear-gradient(${135 - asset.metalness * 60}deg, rgba(255,255,255,${0.05 + shine}) 0%, ${asset.color} 55%, rgba(0,0,0,${0.25 - shine * 0.3}) 100%)`,
            }}
            title={asset.name}
        />
    );
}
